import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  type CreateSocialMediaLinkInput,
  type CreateWorkspaceInput,
  type WorkspaceDto,
  type WorkspaceInput,
  workspaceService,
  type WorkspaceVisualIdentityInput,
} from '@/services/workspace.service';
import { billingService } from '@/services/billing.service';
import type { CancelSubscriptionInput } from '@/features/billing/types';
import { mapApiError } from '@/lib/mapApiError';
import { getStoredWorkspaceId, storedWorkspaceIdToRestore } from '@/lib/storedWorkspaceRestore';
import { normalizeAxiosError } from '@/http/errors';
import type {
  Workspace,
  WorkspaceIntegration,
  WorkspaceIntegrationMetadataItem,
  WorkspaceIntegrationProviderType,
  WorkspaceIntegrationSetupInput,
  WorkspaceSocialMediaLink,
  WorkspaceTeamInviteInput,
  WorkspaceTeamPage,
  WorkspaceTeamRoleUpdateInput,
  WorkspaceVisualIdentity,
} from '@/features/workspace/types';
import type { AppError } from '@/features/auth/types';

const DEFAULT_TEAM_PAGE_SIZE = 20;

export const createWorkspaceThunk = createAsyncThunk<
  void,
  CreateWorkspaceInput,
  { rejectValue: AppError }
>('workspace/createWorkspace', async (input, { rejectWithValue }) => {
  try {
    await workspaceService.create(input);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

const toAppError = (err: unknown): AppError => {
  const e = err as { code?: string; message?: string; status?: number };
  return {
    code: e.code ?? 'UNKNOWN',
    message: mapApiError(e.code ?? 'UNKNOWN', e.status),
  };
};

const INTEGRATION_NOT_CONNECTED_MESSAGE = 'HTTP.ERROR.404.INTEGRATION.NOT_CONNECTED';

type WorkspaceIntegrationMetadataReject = AppError & {
  integrationNotConnected?: boolean;
};

const loadTeamMembers = (workspaceId: number, page?: number, size?: number) =>
  workspaceService.getTeamMembers(workspaceId, page ?? 0, size ?? DEFAULT_TEAM_PAGE_SIZE);

export type FetchUserWorkspacesResult = {
  content: Workspace[];
  totalPages: number;
  /** Workspace mémorisé absent de la page chargée, rechargé à part ; `null` s'il est inaccessible. */
  restored: Workspace | null;
};

const toWorkspace = (dto: WorkspaceDto): Workspace => ({
  id: dto.id,
  name: dto.name,
  timezone: dto.timezone,
  currentUserRole: dto.currentUserRole,
  subscriptionStatus: dto.subscriptionStatus,
  billingCurrency: dto.billingCurrency,
  legacyTrialEndDate: dto.legacyTrialEndDate ?? null,
  promoCode: dto.promoCode ?? null,
  productAccessStatus: dto.productAccessStatus,
  productAccessPauseEndsAt: dto.productAccessPauseEndsAt ?? null,
  billingRegularizationBanner: dto.billingRegularizationBanner ?? null,
  camLayout: dto.camLayout ?? null,
  visualIdentity: null,
  socials: [],
});

export const fetchUserWorkspaces = createAsyncThunk<
  FetchUserWorkspacesResult,
  { page?: number },
  { rejectValue: AppError }
>('workspace/fetchUserWorkspaces', async ({ page }, { getState, rejectWithValue }) => {
  try {
    const { content, totalPages } = await workspaceService.getAll(page ?? 0, 100);
    const { workspace } = getState() as { workspace: { currentWorkspace: Workspace | null } };
    const restoreId = storedWorkspaceIdToRestore({
      hasCurrentWorkspace: workspace.currentWorkspace != null,
      storedId: getStoredWorkspaceId(),
      loadedIds: content.map((w) => w.id),
      totalPages,
    });
    const restored =
      restoreId == null
        ? null
        : await workspaceService
            .getById(restoreId)
            .then(toWorkspace)
            .catch(() => null);
    return { content, totalPages, restored };
  } catch (err) {
    const e = err as { code?: string; message?: string };
    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: e.message ?? 'errors.generic',
    });
  }
});

export const fetchWorkspaceDetails = createAsyncThunk<
  { visualIdentity: WorkspaceVisualIdentity | null; socials: WorkspaceSocialMediaLink[] },
  number,
  { rejectValue: AppError }
>('workspace/fetchWorkspaceDetails', async (workspaceId, { rejectWithValue }) => {
  try {
    const [visualIdentity, socials] = await Promise.all([
      workspaceService.getVisualIdentity(workspaceId),
      workspaceService.getSocialMediaLinks(workspaceId),
    ]);
    return { visualIdentity: visualIdentity ?? null, socials: socials ?? [] };
  } catch (err) {
    const e = err as { code?: string; message?: string };
    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: e.message ?? 'errors.generic',
    });
  }
});

export const updateVisualIdentity = createAsyncThunk<
  WorkspaceVisualIdentity | null,
  { workspaceId: number; input: WorkspaceVisualIdentityInput },
  { rejectValue: AppError }
>('workspace/updateVisualIdentity', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    await workspaceService.updateVisualIdentity(workspaceId, input);
    const updatedVisualIdentity = await workspaceService.getVisualIdentity(workspaceId);
    return updatedVisualIdentity;
  } catch (err) {
    const e = err as { code?: string; message?: string };
    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: e.message ?? 'errors.generic',
    });
  }
});

export const uploadLogo = createAsyncThunk<
  WorkspaceVisualIdentity | null,
  { workspaceId: number; file: File },
  { rejectValue: AppError }
>('workspace/uploadLogo', async ({ workspaceId, file }, { rejectWithValue }) => {
  try {
    await workspaceService.uploadLogo(workspaceId, file);
    const updatedVisualIdentity = await workspaceService.getVisualIdentity(workspaceId);
    return updatedVisualIdentity;
  } catch (err) {
    const e = err as { code?: string; message?: string };
    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: e.message ?? 'errors.generic',
    });
  }
});

export const createSocialMediaLink = createAsyncThunk<
  WorkspaceSocialMediaLink[],
  { workspaceId: number; input: CreateSocialMediaLinkInput },
  { rejectValue: AppError }
>('workspace/createSocialMediaLink', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    await workspaceService.createSocialMediaLink(workspaceId, input);
    const socials = await workspaceService.getSocialMediaLinks(workspaceId);
    return socials;
  } catch (err) {
    const e = err as { code?: string; message?: string; status?: number };
    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: mapApiError(e.code ?? 'UNKNOWN', e.status),
    });
  }
});

export const deleteSocialMediaLink = createAsyncThunk<
  WorkspaceSocialMediaLink[],
  { workspaceId: number; id: number },
  { rejectValue: AppError }
>('workspace/deleteSocialMediaLink', async ({ workspaceId, id }, { rejectWithValue }) => {
  try {
    await workspaceService.deleteSocialMediaLink(workspaceId, id);
    const socials = await workspaceService.getSocialMediaLinks(workspaceId);
    return socials;
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

type TeamPaginationArgs = {
  workspaceId: number;
  page?: number;
  size?: number;
};

export const fetchWorkspaceTeam = createAsyncThunk<
  WorkspaceTeamPage,
  TeamPaginationArgs,
  { rejectValue: AppError }
>('workspace/fetchWorkspaceTeam', async ({ workspaceId, page, size }, { rejectWithValue }) => {
  try {
    return await loadTeamMembers(workspaceId, page, size);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const inviteWorkspaceTeamMember = createAsyncThunk<
  WorkspaceTeamPage,
  TeamPaginationArgs & { input: WorkspaceTeamInviteInput },
  { rejectValue: AppError }
>(
  'workspace/inviteWorkspaceTeamMember',
  async ({ workspaceId, page, size, input }, { rejectWithValue }) => {
    try {
      await workspaceService.inviteTeamMember(workspaceId, input);
      return await loadTeamMembers(workspaceId, page, size);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const updateWorkspaceTeamMemberRole = createAsyncThunk<
  WorkspaceTeamPage,
  TeamPaginationArgs & { userId: string; input: WorkspaceTeamRoleUpdateInput },
  { rejectValue: AppError }
>(
  'workspace/updateWorkspaceTeamMemberRole',
  async ({ workspaceId, userId, input, page, size }, { rejectWithValue }) => {
    try {
      await workspaceService.updateTeamMemberRole(workspaceId, userId, input);
      return await loadTeamMembers(workspaceId, page, size);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const removeWorkspaceTeamMember = createAsyncThunk<
  WorkspaceTeamPage,
  TeamPaginationArgs & { userId: string },
  { rejectValue: AppError }
>(
  'workspace/removeWorkspaceTeamMember',
  async ({ workspaceId, userId, page, size }, { rejectWithValue }) => {
    try {
      await workspaceService.removeTeamMember(workspaceId, userId);
      return await loadTeamMembers(workspaceId, page, size);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const transferWorkspaceOwnership = createAsyncThunk<
  WorkspaceTeamPage,
  TeamPaginationArgs & {
    newOwnerUserIdentifier: string;
  },
  { rejectValue: AppError }
>(
  'workspace/transferWorkspaceOwnership',
  async ({ workspaceId, newOwnerUserIdentifier, page, size }, { rejectWithValue }) => {
    try {
      await workspaceService.transferOwnership(workspaceId, {
        newOwnerUserIdentifier,
      });
      return await loadTeamMembers(workspaceId, page, size);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const fetchWorkspaceById = createAsyncThunk<WorkspaceDto, number, { rejectValue: AppError }>(
  'workspace/fetchWorkspaceById',
  async (workspaceId, { rejectWithValue }) => {
    try {
      return await workspaceService.getById(workspaceId);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const fetchWorkspaceIntegrations = createAsyncThunk<
  WorkspaceIntegration[],
  number,
  { rejectValue: AppError }
>('workspace/fetchWorkspaceIntegrations', async (workspaceId, { rejectWithValue }) => {
  try {
    return await workspaceService.getIntegrations(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const setupWorkspaceIntegration = createAsyncThunk<
  WorkspaceIntegration[],
  {
    workspaceId: number;
    providerType: WorkspaceIntegrationProviderType;
    input: WorkspaceIntegrationSetupInput;
  },
  { rejectValue: AppError }
>(
  'workspace/setupWorkspaceIntegration',
  async ({ workspaceId, providerType, input }, { rejectWithValue }) => {
    try {
      await workspaceService.setupIntegration(workspaceId, providerType, input);
      return await workspaceService.getIntegrations(workspaceId);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const deleteWorkspaceIntegration = createAsyncThunk<
  WorkspaceIntegration[],
  { workspaceId: number; providerType: WorkspaceIntegrationProviderType },
  { rejectValue: AppError }
>(
  'workspace/deleteWorkspaceIntegration',
  async ({ workspaceId, providerType }, { rejectWithValue }) => {
    try {
      await workspaceService.deleteIntegration(workspaceId, providerType);
      return await workspaceService.getIntegrations(workspaceId);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const fetchWorkspaceIntegrationMetadata = createAsyncThunk<
  WorkspaceIntegrationMetadataItem[],
  {
    workspaceId: number;
    providerType: WorkspaceIntegrationProviderType;
    metadataType: string;
  },
  { rejectValue: WorkspaceIntegrationMetadataReject }
>(
  'workspace/fetchWorkspaceIntegrationMetadata',
  async ({ workspaceId, providerType, metadataType }, { rejectWithValue }) => {
    try {
      return await workspaceService.getIntegrationMetadata(workspaceId, providerType, metadataType);
    } catch (err) {
      const appErr = normalizeAxiosError(err);
      const integrationNotConnected = appErr.message === INTEGRATION_NOT_CONNECTED_MESSAGE;
      return rejectWithValue({
        ...toAppError(err),
        integrationNotConnected,
      });
    }
  },
);

export const updateWorkspace = createAsyncThunk<
  WorkspaceDto,
  { workspaceId: number; input: WorkspaceInput },
  { rejectValue: AppError }
>('workspace/updateWorkspace', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    await workspaceService.update(workspaceId, input);
    return await workspaceService.getById(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const cancelWorkspaceSubscription = createAsyncThunk<
  void,
  { workspaceId: number; input: CancelSubscriptionInput },
  { rejectValue: AppError }
>('workspace/cancelSubscription', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    await billingService.cancelSubscription(workspaceId, input);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});
